﻿namespace Illy.SafeStack

module Validation = 
    [<RequireQualifiedAccess>]
    type ValidationResult<'error, 'warning> =
        | Passed
        | Warned of 'warning list
        | Failed of 'error list * 'warning list

    type NoWarning = NoWarning

    let combine =
        function
        | ValidationResult.Passed,ValidationResult.Passed -> ValidationResult.Passed
        | ValidationResult.Passed,ValidationResult.Failed (errs, warnings)
        | ValidationResult.Failed (errs, warnings),ValidationResult.Passed -> ValidationResult.Failed (errs, warnings)
        | ValidationResult.Failed (errs1, warnings1), ValidationResult.Failed (errs2, warnings2) -> ValidationResult.Failed (errs1@errs2,warnings1@warnings2)
        | ValidationResult.Passed,ValidationResult.Warned warnings
        | ValidationResult.Warned warnings, ValidationResult.Passed -> ValidationResult.Warned warnings
        | ValidationResult.Warned warnings1, ValidationResult.Failed (errs, warnings2)
        | ValidationResult.Failed (errs, warnings1), ValidationResult.Warned warnings2 -> ValidationResult.Failed (errs,warnings1@warnings2)
        | ValidationResult.Warned warnings1, ValidationResult.Warned warnings2 -> ValidationResult.Warned (warnings1@warnings2)
    
    let combineMany = 
        function
        | [] -> ValidationResult.Passed 
        | x -> List.reduce (fun x y -> combine (x,y)) x

    type ValidationBuilder() =
        member this.ReturnFrom x = x
        member this.Combine (a, b) =
            combine (a,b)
        member this.Delay f = f()
        member this.Zero() = ValidationResult.Passed

    let validate = ValidationBuilder()

    let error e = ValidationResult.Failed ([e], [])
    let pass = ValidationResult.Passed
    let warning w = ValidationResult.Warned ([w])

    let isSuccessful confirmedWarnings =
        function
        | ValidationResult.Passed -> true
        | ValidationResult.Warned _ -> confirmedWarnings
        | ValidationResult.Failed (_,_) -> false

    let mapError f =
        function
        | ValidationResult.Passed -> ValidationResult.Passed
        | ValidationResult.Warned w -> ValidationResult.Warned w
        | ValidationResult.Failed (errors, warnings) -> ValidationResult.Failed (List.map f errors, warnings)
    
    let mapWarning f =
        function
        | ValidationResult.Passed -> ValidationResult.Passed
        | ValidationResult.Warned w -> ValidationResult.Warned (List.map f w)
        | ValidationResult.Failed (errors, warnings) -> ValidationResult.Failed (errors, List.map f warnings)

    let mapErrorAndWarning fE fW =
        function
        | ValidationResult.Passed -> ValidationResult.Passed
        | ValidationResult.Warned w -> ValidationResult.Warned (List.map fW w)
        | ValidationResult.Failed (errors, warnings) -> ValidationResult.Failed (List.map fE errors, List.map fW warnings)
    
    ///returns true if the ValidationResult is Warned and false otherwise
    let hasWarnings =
        function
        | ValidationResult.Passed -> false
        | ValidationResult.Warned _ -> true
        | ValidationResult.Failed (_) -> false

    let hasErrors =
        function
        | ValidationResult.Passed -> false
        | ValidationResult.Warned _ -> false
        | ValidationResult.Failed (_) -> true

    ///A set of helper functions for dispatching validations to an entity in a list of entities
    module Collections = 
        let toDefinitionsMap errors = 
            errors 
            |> List.groupBy fst 
            |> List.map (fun (p, errors) -> p, errors |> List.map snd)
            |> Map
        
        let private definitionsOrDefault map mapper p = 
            map 
            |> Map.tryFind p 
            |> Option.map mapper
            |> Option.defaultValue ValidationResult.Passed
        
        let errorsOrDefault map p = 
            definitionsOrDefault map (fun x -> ValidationResult.Failed (x, [])) p
        
        let warningsOrDefault map p = 
            definitionsOrDefault map ValidationResult.Warned p

        let private dispatchValidationsInList validationsMap validationGetter dispatchFunc items =
            items
            |> List.mapi
                (fun p item -> 
                    validationGetter validationsMap (Position p)
                    |> dispatchFunc item)

        let dispatchErrorsInList errorsMap dispatchFunc items = 
            dispatchValidationsInList errorsMap errorsOrDefault dispatchFunc items 

        let dispatchWarningsInList errorsMap dispatchFunc items = 
            dispatchValidationsInList errorsMap warningsOrDefault dispatchFunc items 

module CompositionalValidation =
    open System
    open Validation
    open ROP

    let unwrap =
        function
        | Failure x -> x
        | Success x -> x

    let map fS ff =
        function
        | Success s -> Success (fS s)
        | Failure f -> Failure (ff f) 

    let stopIfNone raise opt prev =
        match opt with
        | None -> Failure (raise prev)
        | Some x -> Success (prev, x)

    let stopIfSome raise opt prev =
        match opt with
        | None -> Success prev
        | Some _ -> Failure (raise prev)

    let passIfSome opt p = stopIfSome (fun _ -> pass) opt p
    let passIfNone opt p = stopIfNone (fun _ -> pass) opt p

    let mapResult f x = map f id x |> unwrap
    let firstResult x = map fst id x |> unwrap
    
    let otherwisePass =
        function
        | Failure x -> x
        | Success _ -> pass

    let (!!) = succeed
    let (!!!) x = oneTrack (fun _ -> x)
    let (>>.) p n = p >>= (snd >> n) 
    let (.>>) p n = p >>= (fst >> n)

    /// Operators
    let (>->) x y = x >> mapError y
    let (>-|>) x y = x >> mapErrorAndWarning id y

    let optionalString str =
        if String.IsNullOrWhiteSpace str then None else Some str

    let concatOptional : string list -> string option = String.concat " " >> optionalString

    let addToAcc (acc: string list []) index str = 
        acc.[index] <- str :: acc.[index]
        acc

    ///Short-circuit the validation if condition is false
    let stopIfTrue raise condition prev =
        if condition 
        then Failure raise
        else Success prev

    let stopIfFalse<'err, 'warn> (raise: ValidationResult<'err, 'warn>) condition (prev: ValidationResult<'err, 'warn>) =
        if (not condition)
        then Failure raise
        else Success prev

    let conditional raise condition prev =
        if condition prev
        then Failure raise
        else Success prev
    
    let skipUnless condition = stopIfFalse pass condition
    let passIf condition = stopIfTrue pass condition

module SharedValidations =
    open System
    open Validation
    open ROP
    open CompositionalValidation

    [<RequireQualifiedAccess>]
    type OptionValidations =
        | None
        member this.toString() =
            match this with
            | None -> "Must be entered."

    let optionNone =
        function
        | Some _ -> ValidationResult.Passed
        | None -> error OptionValidations.None
    
    [<RequireQualifiedAccess>]
    type StringValidations =
        | MaxLength of int
        | NotEntered
        member this.toString() =
            match this with
            | MaxLength i -> sprintf "Max length is restricted to %d." i
            | NotEntered -> "This field is required."
    
    let validateString maxLength isMandatory (value: string option) = 
        validate {
            
            return! 
                !!(error (StringValidations.NotEntered))
                >>= skipUnless isMandatory
                >>= conditional pass 
                    (fun _ -> 
                        match value with
                        | None -> false
                        | Some v -> String.IsNullOrWhiteSpace(v) |> not )
                |> unwrap
            
            return! 
                !!(error (StringValidations.MaxLength maxLength))
                >>= passIfNone value
                >>= conditional pass (fun (_, v) -> v.Length <= maxLength)
                |> firstResult 
        }
    
    type ListOptionValidations = 
        | NotEntered
        member this.toString() =
            match this with
            | NotEntered -> "This field is required."

    let validateListOption (value: ListOption option) = 
        validate {
            return! 
                !!(error ListOptionValidations.NotEntered)
                >>= passIfSome value
                |> unwrap
        }
    
    type DateValidations =
    | MustBeSet
    | CannotBeInTheFuture
    with 
        member this.toString() = 
            match this with 
            | MustBeSet -> "Must be set"
            | CannotBeInTheFuture -> "Date cannot be in the future"

    let validateDate isMandatory allowFutureDates (model: DateTime option) =
        validate {
            return! 
                !!(error DateValidations.MustBeSet)
                >>= skipUnless isMandatory
                >>= passIfSome model
                |> unwrap
        
            return! 
                !!(error DateValidations.CannotBeInTheFuture)
                >>= skipUnless (not allowFutureDates)
                >>= passIfNone model
                >>= conditional pass (fun (_, d) -> d <= DateTime.Today)
                |> firstResult
        }
    
    type MultiSelectValidations = 
        | MustBeSet
        with member this.toString() = match this with | MustBeSet -> "At least 1 option must be selected"

    let validateMultiSelect (model: ListOption list) = 
        validate {
            return! !!(error MultiSelectValidations.MustBeSet)
                >>= conditional pass (fun _ -> model |> List.isEmpty |> not)
                |> unwrap
        }

    type IntegerValidations =
        | MinValue of int
        | MaxValue of int
        | MustBeSet

    module IntegerValidations = 
        let toString = 
            function 
            | MinValue i -> sprintf "Value must be greater than %d." i
            | MaxValue i -> sprintf "Value must be less than %d." i
            | MustBeSet -> "This field is required."

    let validateInteger isMandatory minValue maxValue (value: int option) = 
        validate {
            return! !!(error IntegerValidations.MustBeSet)
                >>= skipUnless isMandatory
                >>= passIfSome value
                |> unwrap

            match minValue with
            | Some min ->
                return!
                    !!(error <| IntegerValidations.MinValue min)
                    >>= passIfNone value
                    >>= conditional pass (fun (_, v) -> v >= min)
                    |> firstResult
            | None -> ()

            match maxValue with
            | Some max ->
                return!
                    !!(error <| IntegerValidations.MaxValue max)
                    >>= passIfNone value
                    >>= conditional pass (fun (_, v) -> v <= max)
                    |> firstResult
            | None -> ()
        }